import BasePage from "../../../components/layout/BasePage"
import HeadBanner from "../../../components/elements/BaseHeadBanner"
import BillTableRow from "../components/bill/BillTableRow"
import { useContext, useEffect, useMemo, useState } from "react"
import { ModalContext } from "../../../components/contexts/ModalContext"
import { useQuery } from "@tanstack/react-query"
import Loading from "../../../components/common/Loading"
import ErrorPage from "../../404/ErrorPage"
import AddBillModal from "../components/addBillModal/AddBillModal"
import { getDataFromSessionStorage, useDelayWhenSearchWithPagination, useResetPageOnFilterChange } from "../../../services/functions"
import { BillGet } from "../../../data/Types"
import { P } from "../../../auth/permissions"
import { Pagination } from "antd"
import { getPaginatedData } from "../../api/apiCalls"
import BaseFilterModal from "../../../components/elements/BaseFilterModal"
import { ReactComponent as PLUSINCIRCLE } from "../../../assets/PlusInCircleIcon.svg"
import { ReactComponent as DOWNLOAD } from "../../../assets/DownloadICON.svg"
import BaseGrowBody from "../../../components/elements/BaseGrowBody"
import ExportBillsModal from "../components/ExportBillsModal"
import BaseTable, { ColumnType } from "../../../components/elements/BaseTable"
import SearchbarAndNavigationButtons from "../components/SearchbarAndNavigationButtons"
import FloatingActionButton from "../components/FloatingActionButton"   
import { GeneralQueryKeys, LocalStorageKeys } from "../../../api/constants"
import { Authorized } from "../../../components/authentication/Authorized"
export default function BillPage() {
    // variables for pagination
    const [page, setpage] = useState(getDataFromSessionStorage(LocalStorageKeys.BILL_FILTER + "page") ?? 1)
    const handlePageChange = (page: number) => {
        sessionStorage.setItem(LocalStorageKeys.BILL_FILTER + "page", JSON.stringify(page))
        setpage(page)
    }

    // variables for search in pagination
    const [searchTerm, setsearchTerm] = useState(getDataFromSessionStorage(LocalStorageKeys.BILL_FILTER + "searchTerm"))
    const searchParams = useDelayWhenSearchWithPagination(searchTerm, LocalStorageKeys.BILL_FILTER + "searchTerm")
    const [filterType, setfilterType] = useState(getDataFromSessionStorage(LocalStorageKeys.BILL_FILTER + "billType"))
    const [filterCategorie, setfilterCategorie] = useState(getDataFromSessionStorage(LocalStorageKeys.BILL_FILTER + "billCategorie"))
    const [filterDateSince, setfilterDateSince] = useState(getDataFromSessionStorage(LocalStorageKeys.BILL_FILTER + "dateSince"))
    const [filterDateTo, setfilterDateTo] = useState(getDataFromSessionStorage(LocalStorageKeys.BILL_FILTER + "dateTo"))
    const [invertSort, setinvertSort] = useState<boolean | undefined>(getDataFromSessionStorage(LocalStorageKeys.BILL_FILTER + "invertSort"))
    const [filterArchived, setfilterArchived] = useState<boolean | undefined>(getDataFromSessionStorage(LocalStorageKeys.BILL_FILTER + "archived"))
    const [filterCanceled, setfilterCanceled] = useState<boolean | undefined>(getDataFromSessionStorage(LocalStorageKeys.BILL_FILTER + "canceled"))

    useResetPageOnFilterChange([searchParams, filterType, filterCategorie, filterDateSince, filterDateTo, invertSort, filterArchived, filterCanceled], () =>
        handlePageChange(1)
    )
    
    // Get Data Query
    const queryKey =
        GeneralQueryKeys.BILL_DATA +
        page +
        searchParams +
        filterType +
        filterDateSince +
        filterDateTo +
        filterArchived +
        filterCategorie +
        invertSort +
        filterCanceled
    const { error, data, isLoading } = useQuery({
        queryKey: [queryKey],
        queryFn: () =>
            getPaginatedData({
                pageType: "bill",
                page: page,
                limit: 15,
                search: searchParams,
                startDate: filterDateSince,
                endDate: filterDateTo,
                archived: filterArchived,
                billType: filterType,
                billCategorie: filterCategorie,
                invertSort: invertSort,
                canceled: filterCanceled,
            }),
    })

    const modalCon = useContext(ModalContext)

    const [showFilter, setshowFilter] = useState(false)

    const handleRemoveFilter = () => {
        setfilterType("")
        setfilterCategorie("")
        setfilterDateTo("")
        setfilterDateSince("")
        setfilterArchived(false)
        setfilterCanceled(undefined)

    }

    
    // FRONTEND LOGIC
    const displayedBills = useMemo(() => data?.docs, [data?.docs])
    const billTableColumns: ColumnType[] = [
        {
            label: "Erstellungsdatum",
            responsive: "hidden md:table-cell",
            width: "min-w-[10rem] w-fit",
        },
        {
            label: window.innerWidth > 640 ? "Rechnungsnummer" : "Nr.",
            responsive: "table-cell",
        },
        {
            label: "Typ",
            responsive: "hidden sm:table-cell",
            width: " min-w-[9rem] w-fit",
        },
        {
            label: "Netto",
            width: "sm:min-w-[8rem] w-fit",
            permissions: [P.buchhaltung_read],
        },
        {
            label: "Brutto",
            width: "sm:min-w-[8rem] w-fit",
            responsive: "hidden xl:table-cell",
            permissions: [P.buchhaltung_read],
        },
        {
            label: "Kunde",
            responsive: "table-cell",
        },
        {
            label: "Objekt",
            responsive: "hidden lg:table-cell ",
        },
    ]

    return (
        <BasePage>
            <HeadBanner
                title={"Rechnungen"}
                button={
                    <Authorized anyOf={[P.buchhaltung_read_write, P.buchhaltung_read_write_restricted]}>
                        <div className="flex gap-4">
                            <DOWNLOAD className="w-8 h-8 cursor-pointer hidden md:block" onClick={() => modalCon?.trigger(3)} />
                            <PLUSINCIRCLE className="w-8 h-8 cursor-pointer hidden md:block" onClick={() => modalCon?.trigger(1)} />
                        </div>
                    </Authorized>
                }
            />
            {/* UPPER ROW OF INPUT & BUTTON */}
            <SearchbarAndNavigationButtons
                searchBarPlaceholder="Rechnunsnummer / Kunde / Objekt suchen..."
                searchTerm={searchTerm}
                sortDirection={invertSort ?? false}
                setsearchTerm={setsearchTerm}
                sort={() => setinvertSort(!invertSort)}
                showFilter={() => setshowFilter(!showFilter)}
            />
            {isLoading && <Loading />}
            {error && <ErrorPage />}
            {data && !error && !isLoading && (
                <>
                    {/* TABLE */}
                    <BaseGrowBody classProps=" overflow-x-hidden my-4">
                        <BaseTable
                            columns={billTableColumns}
                            tbody={displayedBills?.map((item: BillGet) => (
                                <BillTableRow
                                    key={item._id}
                                    id={item._id}
                                    title={item.nr}
                                    customer={item?.customer_id?.customer}
                                    date={item.date}
                                    delivery={item.serviceDate}
                                    object={item?.object_id ?? "Keine Angabe"}
                                    netto={item.totalNetto}
                                    brutto={item.totalBrutto}
                                    type={item.billType}
                                    archived={item.archived}
                                    canceled={item.canceled}
                                />
                            ))}
                        />
                        {(!displayedBills || displayedBills.length === 0) && (
                            <p className="text-center text-gray-300 uppercase font-medium pt-4">Keine erstellten Rechnungen</p>
                        )}
                    </BaseGrowBody>
                    <BaseFilterModal
                        visible={showFilter}
                        searchdateSince={filterDateSince}
                        searchdateTo={filterDateTo}
                        changeDateTo={setfilterDateTo}
                        changeDateSince={setfilterDateSince}
                        removeFilter={handleRemoveFilter}
                        searchbillType={filterType}
                        changebillType={setfilterType}
                        searchbillCategorie={filterCategorie}
                        changebillCategorie={setfilterCategorie}
                        setVisible={() => setshowFilter(!showFilter)}
                        searchArchived={filterArchived}
                        changeArchived={setfilterArchived}
                        searchCanceled={filterCanceled}
                        changeCanceled={setfilterCanceled}
                        statusType={"bill"}
                        sessionStorageKey={LocalStorageKeys.BILL_FILTER}
                    />
                    {/* PAGINATION */}
                    <div className="flex justify-center w-full p-4">
                        <Pagination
                            pageSize={15}
                            defaultCurrent={1}
                            current={page}
                            onChange={handlePageChange}
                            total={data.totalDocs}
                            showSizeChanger={false}
                        />
                    </div>
                </>
            )}
            <AddBillModal queryKey={queryKey} />
            <ExportBillsModal modalId={3} close={() => modalCon?.trigger(3)} />
            <FloatingActionButton currentPage="bill" />
        </BasePage>
    )
}
